import {
  getAppStatus,
  registerApplication,
  start,
  addErrorHandler,
  LOAD_ERROR,
  MOUNTED,
  getMountedApps
} from "single-spa";
import {
  constructApplications,
  constructRoutes,
  constructLayoutEngine
} from "single-spa-layout";
import microfrontendLayout from "./microfrontend-layout.html";
import {
  EventBus,
  AuthService,
  Redirection,
  UriLocale,
  CountryService2,
  CookieService,
  ClearSchema
} from "@cytiva-dxda/ps-microfrontend-utils";
import { initRelay, trackPageView, syncTrackingIdentity } from "./tracking";

import { showErrorModal } from "./ui-error/ui-error-modal";
import {
  AP_FOOTER_LOADER,
  AP_HOMEPAGE_LOADER,
  AP_SEARCH_BOX_LOADER,
  AP_NAVIGATION_LOADER,
  AP_KNOWLEDGE_CENTER_LOADER,
  AP_KNOWLEDGE_CENTER_ARTICLE_LOADER,
  AP_TRAINING_LOADER,
  AP_TRAINING_CATALOG_LOADER,
  AP_TRAINING_CATALOG_OBJECT_LOADER,
  AP_TRAINING_CATALOG_MY_COURSES_LOADER,
  AP_SRP_LOADER,
  AP_D3_SEARCH_LOADER,
  AP_CHROMATOGRAPHY_CALCULATORS_LOADER,
  AP_RSF_USER_LOADER,
  AP_RSF_DOWNLOAD_LOADER,
  AP_REQUEST_SAMPLE_LOADER
} from "./skeletons/ap";
import {
  MA_REGISTRATION_LOADER,
  MA_TRACK_ORDER_LOADER,
  MA_ORDER_HISTORY_LOADER,
  MA_EQUIPMENT_LOADER,
  MA_MY_PROFILE_LOADER,
  MA_COMPANY_ADDRESSES_LOADER,
  MA_SETTINGS_LOADER,
  MA_MARKETPLACE_LOADER
} from "./skeletons/ma";
import {
  OC_SPECIAL_OFFERS,
  OC_CUSTOM_PRODUCTS,
  OC_QUICK_ORDER_LOADER,
  OC_QUOTE_REDEMPTION_SEARCH,
  OC_QUOTE_REDEMPTION_DETAILS,
  OC_PRODUCT_DETAILS_PAGE,
  OC_QUOTE_CART_PAGE,
  OC_SHOPPING_CART_PAGE,
  OC_PRODUCT_CATALOG_DOWNLOAD_LOADER,
  OC_PRODUCTS_CATALOG_PAGE,
  OC_PRODUCTS_CATALOG_HOME_PAGE,
  OC_CHECKOUT,
  OC_PARTS_AND_ACCESSORIES_LOADER,
  OC_PRODUCT_AVAILABILITY,
  OC_CONTRACT_ORDER
} from "./skeletons/oc";
import {
  PS_HEADER_LOADER,
  PS_FOOTER_LOADER,
  PS_MASTER_HEADER_LOADER,
  PS_COUNTRY_SELECTION_LOADER
} from "./skeletons/ps";
import {
  SUP_CONTACT_US_LOADER,
  SUP_CELL_THERAPY_CALCULATORS_LOADER,
  SUP_CELL_THERAPY_SELF_FIX_TOOL_LOADER,
  SUP_LEADERSHIP_LOADER,
  SUP_ONLINE_TOOLS_BIACORE_PLASMON_LOADER,
  SUP_ONLINE_TOOLS_CELL_PREPARATION_LOADER,
  SUP_ONLINE_TOOLS_CROSSFLOW_FLOW_LOADER,
  SUP_ONLINE_TOOLS_GENERAL_LOADER,
  SUP_ONLINE_TOOLS_LANDING_PAGE_LOADER,
  SUP_SECURITY_UPDATES_LOADER,
  SUP_SOURCING_LOADER,
  SUP_FIND_DISTRIBUTOR_LOADER,
  SUP_ABOUT_US_PRESS_RELEASES_LOADER,
  SUP_PRESS_RELEASES_DETAILS_LOADER,
  SUP_SOFTWARE_BIACORE_DOWNLOADS_LOADER,
  SUP_WHATMAN_METHODS_AND_STANDARDS_SEARCH_LOADER,
  SUP_EQUIPMENT_REPAIRS_LOADER,
  SUP_WHATMAN_FILTER_SELECTOR_LOADER
} from "./skeletons/sup";
import { SXA_DEFAULT_LOADER } from "./skeletons/sxa";
import {
  HTMLLayoutData,
  ResolvedRoutesConfig
} from "single-spa-layout/dist/types/isomorphic/constructRoutes";
import {
  RouterSkeletonsTargetApps,
  RouteToErrorSkeletonMap,
  RouteToSkeletonMap
} from "./skeletons/common/route-skeleton-config";
import {
  excludeSkeletons,
  injectSkeletonForUrl
} from "./skeletons/common/route-skeleton-logic";
import { PURIFY_APP_LOADER } from "./skeletons/other";

const QUICK_ORDER_APP = "@cytiva/oc-quick-order";
const BULK_UPLOAD_PARCEL = "@cytiva/oc-bulk-upload";
const CHINA_COUNTRY_CODE = "cn";
const SILENT_AUTH_PARAM = "silent_auth";
const SYSTEM_IMPORT_MAX_RETRIES = 1;

const eventBus = new EventBus();
const authService = new AuthService(`${API_BASE_URL}`, document);
const uriLocale = new UriLocale();
const countryService = new CountryService2(`${API_BASE_URL}`);
const cookieService = new CookieService(document);

declare const ACTIVATE_TRACKING_CONSENT: string;

initRelay({
  endpoint: `${API_BASE_URL}/ingest`,
  activateTrackingConsent: ACTIVATE_TRACKING_CONSENT,
  authService
});

const skeletons: Record<string, { desktop: string; mobile?: string }> = {
  PS_HEADER_LOADER,
  PS_FOOTER_LOADER,
  AP_FOOTER_LOADER,
  AP_HOMEPAGE_LOADER,
  MA_REGISTRATION_LOADER,
  MA_TRACK_ORDER_LOADER,
  MA_ORDER_HISTORY_LOADER,
  MA_MY_PROFILE_LOADER,
  MA_COMPANY_ADDRESSES_LOADER,
  MA_SETTINGS_LOADER,
  MA_MARKETPLACE_LOADER,
  OC_SPECIAL_OFFERS,
  OC_CUSTOM_PRODUCTS,
  OC_QUICK_ORDER_LOADER,
  OC_QUOTE_REDEMPTION_SEARCH,
  OC_QUOTE_REDEMPTION_DETAILS,
  OC_PRODUCT_DETAILS_PAGE,
  OC_QUOTE_CART_PAGE,
  OC_SHOPPING_CART_PAGE,
  OC_PRODUCT_CATALOG_DOWNLOAD_LOADER,
  OC_PRODUCTS_CATALOG_PAGE,
  OC_PRODUCTS_CATALOG_HOME_PAGE,
  OC_CHECKOUT,
  OC_PARTS_AND_ACCESSORIES_LOADER,
  OC_PRODUCT_AVAILABILITY,
  OC_CONTRACT_ORDER,
  AP_SEARCH_BOX_LOADER,
  AP_NAVIGATION_LOADER,
  AP_KNOWLEDGE_CENTER_LOADER,
  PS_MASTER_HEADER_LOADER,
  PS_COUNTRY_SELECTION_LOADER,
  AP_KNOWLEDGE_CENTER_ARTICLE_LOADER,
  AP_TRAINING_LOADER,
  AP_TRAINING_CATALOG_LOADER,
  AP_TRAINING_CATALOG_OBJECT_LOADER,
  AP_TRAINING_CATALOG_MY_COURSES_LOADER,
  AP_SRP_LOADER,
  AP_D3_SEARCH_LOADER,
  AP_CHROMATOGRAPHY_CALCULATORS_LOADER,
  AP_RSF_USER_LOADER,
  AP_RSF_DOWNLOAD_LOADER,
  AP_REQUEST_SAMPLE_LOADER,
  SUP_CONTACT_US_LOADER,
  MA_EQUIPMENT_LOADER,
  SUP_CELL_THERAPY_CALCULATORS_LOADER,
  SUP_CELL_THERAPY_SELF_FIX_TOOL_LOADER,
  SUP_ONLINE_TOOLS_BIACORE_PLASMON_LOADER,
  SUP_ONLINE_TOOLS_GENERAL_LOADER,
  SUP_ONLINE_TOOLS_CROSSFLOW_FLOW_LOADER,
  SUP_ONLINE_TOOLS_LANDING_PAGE_LOADER,
  SUP_ONLINE_TOOLS_CELL_PREPARATION_LOADER,
  SUP_SECURITY_UPDATES_LOADER,
  SUP_LEADERSHIP_LOADER,
  SUP_SOURCING_LOADER,
  SUP_FIND_DISTRIBUTOR_LOADER,
  SUP_ABOUT_US_PRESS_RELEASES_LOADER,
  SUP_PRESS_RELEASES_DETAILS_LOADER,
  SUP_SOFTWARE_BIACORE_DOWNLOADS_LOADER,
  SUP_WHATMAN_METHODS_AND_STANDARDS_SEARCH_LOADER,
  SUP_EQUIPMENT_REPAIRS_LOADER,
  SUP_WHATMAN_FILTER_SELECTOR_LOADER,
  SXA_DEFAULT_LOADER,
  PURIFY_APP_LOADER
};

const isMobile = () => window.innerWidth < 768;

function getSkeletons(
  loaders: Record<string, { desktop: string; mobile?: string }>
): Record<string, string> {
  const mobile = isMobile();
  return Object.fromEntries(
    Object.entries(loaders).map(([key, { desktop, mobile: mobileVersion }]) => [
      key,
      mobile && mobileVersion ? mobileVersion : desktop
    ])
  );
}

const data: HTMLLayoutData = {
  loaders: getSkeletons(excludeSkeletons(RouteToSkeletonMap, skeletons)),
  errors: getSkeletons(excludeSkeletons(RouteToSkeletonMap, skeletons)),
  props: {}
};

addErrorHandler((err) => {
  getAppStatus(err.appOrParcelName) === LOAD_ERROR &&
    System.delete(System.resolve(err.appOrParcelName));
  window.DD_RUM.addError(err);
  stopLoadingAnimation(err.appOrParcelName);
  showErrorModal();
});

function stopLoadingAnimation(appName: string) {
  const element = document.getElementById(`single-spa-application:${appName}`);
  element.classList.add("-no--animation");
}

function getCacheBustedUrl(url: string): string {
  const separator = url.includes("?") ? "&" : "?";
  return `${url}${separator}_cb=${Date.now()}`;
}

function resolveSystemImportUrl(name: string): string {
  try {
    return System.resolve(name);
  } catch {
    return name;
  }
}

async function loadWithRetry(
  name: string,
  maxRetries = SYSTEM_IMPORT_MAX_RETRIES
): Promise<any> {
  const resolvedUrl = resolveSystemImportUrl(name);

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      const importUrl = attempt === 0 ? name : getCacheBustedUrl(resolvedUrl);
      if (attempt > 0) {
        System.delete(name);
      }
      return await System.import(importUrl);
    } catch (error) {
      if (attempt === maxRetries) {
        throw error;
      }

      console.warn(
        `[${name}] Load failed (attempt ${attempt + 1}), retrying with cache-bust...`,
        error
      );
      System.delete(resolvedUrl);
    }
  }

  throw new Error(`[${name}] Unexpected import retry failure.`);
}

function cleanUpAuth0RedirectUrl(url: string) {
  const auth0RedirectUrlKey: string = "auth0RedirectUrl";
  const referrer: string = document.referrer.length
    ? new URL(document.referrer).pathname
    : "";

  if (
    !referrer.endsWith("/auth/login/callback") &&
    !url.includes("/account/registration/")
  ) {
    localStorage.removeItem(auth0RedirectUrlKey);
    cookieService.delete(auth0RedirectUrlKey, { path: "/" });
  }
}

let resolveRouting;

function waitForRouting() {
  return new Promise((resolve) => {
    resolveRouting = resolve;
  });
}

function resolveRoutingAfterIdentitySync(preloadedUserId?: string) {
  const syncPromise =
    preloadedUserId != null && preloadedUserId !== ""
      ? syncTrackingIdentity(authService, { userId: preloadedUserId })
      : syncTrackingIdentity(authService);
  syncPromise.finally(() => resolveRouting(true));
}

const routes: ResolvedRoutesConfig = constructRoutes(microfrontendLayout, data);
const applications = constructApplications({
  routes,
  loadApp({ name }) {
    return loadWithRetry(name);
  }
});

// Make the two homepage apps mutually exclusive on /:language/:country:
// - ap-homepage  -> active for every language EXCEPT "zh".
// - ps-static-html-wrapper -> active on the homepage ONLY for "zh",
//   while preserving every other route where it is registered via the layout HTML.
const ZH_LANGUAGE_CODE = "zh";

function getHomepageLanguage(loc: Location): string | null {
  const segments = loc.pathname.replace(/\/+$/, "").split("/").filter(Boolean);
  return segments.length === 2 ? segments[0].toLowerCase() : null;
}

applications.forEach((app) => {
  if (app.name === "@cytiva/ap-homepage") {
    app.activeWhen = [
      (loc: Location) => {
        const lang = getHomepageLanguage(loc);
        return lang !== null && lang !== ZH_LANGUAGE_CODE;
      }
    ];
  } else if (app.name === "@cytiva-dxda/ps-static-html-wrapper") {
    const originalActiveWhen = Array.isArray(app.activeWhen)
      ? app.activeWhen
      : [app.activeWhen];
    app.activeWhen = [
      (loc: Location) => {
        const lang = getHomepageLanguage(loc);
        // On the homepage, only mount for "zh".
        if (lang !== null) return lang === ZH_LANGUAGE_CODE;
        // Preserve all other routes from the layout HTML.
        return originalActiveWhen.some(
          (fn) =>
            typeof fn === "function" && (fn as (l: Location) => boolean)(loc)
        );
      }
    ];
  }
});

const layoutEngine = constructLayoutEngine({ routes, applications });

if (RouterSkeletonsTargetApps.size > 0) {
  window.addEventListener(
    "single-spa:before-mount-routing-event",
    (evt: CustomEvent) => {
      const isMobileFlag = isMobile();
      const { appsByNewStatus } = evt.detail;

      if (appsByNewStatus.MOUNTED) {
        appsByNewStatus.MOUNTED.forEach((appName: string) => {
          if (RouterSkeletonsTargetApps.has(appName)) {
            injectSkeletonForUrl(
              appName,
              window.location.href,
              isMobileFlag,
              RouteToSkeletonMap,
              skeletons
            );
          }
        });
      }
    }
  );

  window.addEventListener("single-spa:app-change", (evt: CustomEvent) => {
    const isMobileFlag = isMobile();
    const { appsByNewStatus } = evt.detail;

    if (appsByNewStatus.LOAD_ERROR) {
      appsByNewStatus.LOAD_ERROR.forEach((appName: string) => {
        if (RouterSkeletonsTargetApps.has(appName)) {
          injectSkeletonForUrl(
            appName,
            window.location.href,
            isMobileFlag,
            RouteToErrorSkeletonMap,
            skeletons
          );
        }
      });
    }
  });
}

waitForRouting().then(() => applications.forEach(registerApplication));

layoutEngine.activate();
start();

eventBus.on("manage-cookies", () => {
  window.OneTrust?.ToggleInfoDisplay();
});

eventBus.on("ma-panel-trigger", () => {
  if (!getAppStatus("@cytiva/ma-panel")) {
    registerApplication({
      name: "@cytiva/ma-panel",
      app: () => loadWithRetry("@cytiva/ma-panel") as any,
      activeWhen: ""
    });
  }
});

eventBus.on("oc-quote-cart-sidebar-open", (details: any) => {
  const appName: string = "@cytiva/oc-quote-cart-sidebar";
  if (!getAppStatus(appName)) {
    registerApplication({
      name: appName,
      app: () => loadWithRetry(appName) as any,
      activeWhen: "",
      customProps: details.detail
    });
  }
});

eventBus.on("oc-shopping-cart-sidebar-open", (details: any) => {
  const appName: string = "@cytiva/oc-shopping-cart-sidebar";
  if (!getAppStatus(appName)) {
    registerApplication({
      name: appName,
      app: () => loadWithRetry(appName) as any,
      activeWhen: "",
      customProps: details.detail
    });
  }
});

eventBus.on("oc-quote-cart:bulk-upload-modal-open", (details: any) => {
  if (getAppStatus(QUICK_ORDER_APP) === "MOUNTED") {
    eventBus.emit("oc-sidebar-link-open-bulk-upload-modal");
    return;
  }

  if (!getAppStatus(BULK_UPLOAD_PARCEL)) {
    registerApplication({
      name: BULK_UPLOAD_PARCEL,
      app: () => loadWithRetry(BULK_UPLOAD_PARCEL) as any,
      activeWhen: "",
      customProps: details.detail
    });
  } else {
    eventBus.emit("oc-sidebar-link-open-bulk-upload-modal");
    return;
  }
});

window.addEventListener(
  "single-spa:before-routing-event",
  ({
    detail: { oldUrl, newUrl, cancelNavigation, newAppStatuses }
  }: CustomEvent) => {
    ClearSchema();
    const targetUrl = new URL(newUrl);

    const isInitialLoad = getMountedApps().length === 0;

    if (!isInitialLoad) {
      const oldUrlWithoutHash = oldUrl.split("#")[0].replace(/\/$/, "");
      const newUrlWithoutHash = newUrl.split("#")[0].replace(/\/$/, "");
      const oldHash = oldUrl.split("#")[1];
      const newHash = newUrl.split("#")[1];

      if (
        oldUrlWithoutHash &&
        newUrlWithoutHash &&
        oldUrlWithoutHash === newUrlWithoutHash &&
        oldHash !== newHash &&
        (oldHash || newHash)
      ) {
        cancelNavigation();
        trackPageView(newUrl);
        return;
      }
    }

    cleanUpAuth0RedirectUrl(targetUrl.pathname);

    if (!uriLocale.isValidLocaleUriNoValidation(targetUrl.pathname)) {
      cancelNavigation(true);
      const fallbackLang =
        cookieService.get("ls_language")?.toLowerCase() || "en";
      const fallbackCountry =
        cookieService.get("ls_country")?.toLowerCase() || "us";

      const newPath = `/${fallbackLang}/${fallbackCountry}${targetUrl.pathname}${targetUrl.search}${targetUrl.hash}`;

      setTimeout(() => {
        window.history.replaceState(null, "", newPath);
      }, 0);

      return;
    }

    if (!authService.canActivate(newUrl)) {
      cancelNavigation(true);
      const urlToRedirect =
        authService.getAlternativeUrlForNotActiveRoute(newUrl);

      if (urlToRedirect !== Redirection.Login) {
        window.location.assign(`${window.location.origin}/${urlToRedirect}`);
      }
      return;
    }

    const staticWrapperApp = "@cytiva-dxda/ps-static-html-wrapper";
    const isCurrentlyMounted = getAppStatus(staticWrapperApp) === MOUNTED;
    const willBeMounted = newAppStatuses?.[staticWrapperApp] === MOUNTED;
    if (isCurrentlyMounted || (willBeMounted && !isInitialLoad)) {
      const oldUrlWithoutHash = oldUrl.split("#")[0].replace(/\/$/, "");
      const newUrlWithoutHash = newUrl.split("#")[0].replace(/\/$/, "");
      if (oldUrlWithoutHash === newUrlWithoutHash) {
        resolveRoutingAfterIdentitySync();
        return;
      }
      // force hard page load when SPA routing to or from static HTML pages
      cancelNavigation(true);
      window.location.assign(newUrl);
      return;
    }

    if (getMountedApps().length === 0) {
      authService.isAuthenticated().then((isAuthenticated) => {
        if (!isAuthenticated) {
          resolveRoutingAfterIdentitySync();
          return;
        }

        const targetUrl = new URL(newUrl);
        const locale = uriLocale.getLocaleFromUri(targetUrl.pathname);

        if (!locale?.countryCode) {
          resolveRoutingAfterIdentitySync();
          return;
        }

        authService
          .getUserInfo()
          .then((response) => response.json())
          .then((data) => {
            const userCountry = data?.user_metadata?.country?.toLowerCase();

            if (!userCountry || userCountry === locale.countryCode) {
              resolveRoutingAfterIdentitySync(data?.userId);
              return;
            }

            countryService
              .getCountryInfo(userCountry.toUpperCase(), "ps-app-shell")
              .then((countryResponse) => countryResponse.json())
              .then((countryData) => {
                const defaultLanguage = countryData?.defaultLanguage;

                if (defaultLanguage) {
                  const redirectOpts =
                    data?.userId != null && data.userId !== ""
                      ? { userId: data.userId as string }
                      : undefined;
                  void syncTrackingIdentity(authService, redirectOpts).then(
                    () => {
                      cancelNavigation(true);

                      const correctedPath = targetUrl.pathname.replace(
                        `/${locale.languageCode}/${locale.countryCode}`,
                        `/${defaultLanguage}/${userCountry}`
                      );

                      const isDomainChange =
                        (userCountry === CHINA_COUNTRY_CODE) !==
                        (locale.countryCode === CHINA_COUNTRY_CODE);

                      const searchParams = new URLSearchParams(
                        targetUrl.search
                      );
                      if (isDomainChange) {
                        searchParams.set(SILENT_AUTH_PARAM, "true");
                      }

                      const redirectUrl = new URL(
                        correctedPath,
                        window.location.origin
                      );
                      redirectUrl.search = searchParams.toString();
                      redirectUrl.hash = targetUrl.hash;

                      window.location.replace(redirectUrl.href);
                    }
                  );
                  return;
                } else {
                  resolveRoutingAfterIdentitySync(data?.userId);
                }
              });
          });
      });

      return;
    }

    resolveRoutingAfterIdentitySync();
  }
);

window.addEventListener(
  "single-spa:routing-event",
  ({ detail: { oldUrl, newUrl, navigationIsCanceled } }: CustomEvent) => {
    if (navigationIsCanceled) {
      return;
    }

    const oldPath = new URL(oldUrl).pathname;
    const newPath = new URL(newUrl).pathname;

    // Suppress page view only if pathname didn't change (query params or hash only)
    // Allow page view on initial navigation where oldUrl === newUrl
    if (oldPath === newPath && oldUrl !== newUrl) {
      return;
    }

    trackPageView(newUrl);

    if (
      getAppStatus("@cytiva-dxda/ps-sxa-wrapper") === MOUNTED &&
      oldUrl !== newUrl
    ) {
      eventBus.emit("ps-sxa-wrapper:routing-event", {
        newUrl
      });
    }

    if (oldPath !== newPath) {
      window.scrollTo(0, 0);
    }
  }
);

window.addEventListener("unload", () => {
  // Use of an unload event listener makes the page ineligible for bfcache.
  // As a result, resolves the issue with the unloaded components after forward/back navigation.
  // See more: https://web.dev/articles/bfcache
});
